import { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import RemoveIcon from "@mui/icons-material/CloseOutlined"

import { roleApi, userApi, vaultApi } from "../../api"
import { useAuth } from "../../context/AuthContext"
import PersonSelect from "../../components/PersonSelect"
import UserAvatar from "../../components/UserAvatar"
import { LoadingRows } from "../../components/LoadingState"

/**
 * Handing a credential to somebody.
 *
 * ── Why there is no "everybody here" ────────────────────────────────────────
 *
 * The document store offers it and this does not. A password shared with a
 * whole organization is a password on a noticeboard, and the problem the vault
 * exists to solve — somebody leaves and nobody can say what they had — comes
 * straight back, because the answer becomes "everyone".
 *
 * ── Why a role is still offered ─────────────────────────────────────────────
 *
 * "Whoever is doing payroll" is a real answer and it maintains itself: the
 * person who takes the job gets the login and the person who leaves it stops
 * having it, with nobody remembering to make that happen. The trail still names
 * the individual who revealed it.
 */
/**
 * The directory, minus the person reading it.
 *
 * You cannot share something with yourself — you own it, and the server refuses
 * it — so your own name in a list of who ELSE has this is an option whose only
 * outcome is an error. Outside the component so it is not a new function on
 * every render, which is what an effect using it would then re-run on.
 */
function withoutMe(rows, viewerId) {
  return (rows || []).filter((person) => Number(person.id) !== Number(viewerId))
}

export default function VaultShareDialog({ item, clientId, onClose, onChanged }) {
  const scope = clientId ? { client_id: clientId } : undefined
  const { user } = useAuth()

  /*
   * The directory, minus the person reading it.
   *
   * You cannot share something with yourself — you own it, the server refuses
   * it, and offering your own name in a list called "who else has this" is an
   * option whose only outcome is an error. Applied wherever the list is set,
   * including after a search, or typing your own name brings it back.
   */
  const [shares, setShares] = useState(null)
  const [people, setPeople] = useState([])
  const [roles, setRoles] = useState([])
  const [subjectType, setSubjectType] = useState("user")
  const [subjectId, setSubjectId] = useState("")
  const [access, setAccess] = useState("use")
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    if (!item) return
    setShares(null)
    try {
      const payload = await vaultApi.shares(item.id, scope)
      setShares(payload.shares || [])
    } catch (err) {
      setError(err.userMessage || "The sharing list could not be loaded.")
      setShares([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, clientId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!item) return
    setSubjectId("")
    setAccess("use")
    setError(null)
    userApi
      .directory("")
      .then((payload) => setPeople(withoutMe(payload.users, user?.id)))
      .catch(() => setPeople([]))
    roleApi
      .list()
      .then((payload) => setRoles(payload.roles || []))
      .catch(() => setRoles([]))
  }, [item, user?.id])

  const add = async () => {
    if (!subjectId) return
    setSaving(true)
    setError(null)
    try {
      await vaultApi.share(item.id, { ...(clientId ? { client_id: clientId } : {}), subject_type: subjectType, subject_id: subjectId, access })
      setSubjectId("")
      await load()
      onChanged?.()
    } catch (err) {
      setError(err.userMessage || "That could not be shared.")
    } finally {
      setSaving(false)
    }
  }

  const remove = async (share) => {
    try {
      await vaultApi.unshare(item.id, share.id, scope)
      await load()
      onChanged?.()
    } catch (err) {
      setError(err.userMessage || "That share could not be removed.")
    }
  }

  return (
    <Dialog open={Boolean(item)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Share “{item?.name}”
        <Typography variant="caption" color="text.secondary" display="block">
          They will be able to reveal it, and every time they do it is recorded against their name.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {/*
          * Wraps, and the button is never the thing that falls off.
          *
          * Three selects and a button do not fit across a 600px dialog, and a
          * fixed row simply clipped the Share button at the right edge — the
          * one control the dialog exists for. They flow instead: each field
          * takes what it needs down to 170px and drops to the next line, and
          * the button sits on its own row aligned right, where it is never
          * competing for width with anything.
          */}
        <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Share with"
            value={subjectType}
            onChange={(event) => {
              setSubjectType(event.target.value)
              setSubjectId("")
            }}
            sx={{ flex: "1 1 170px", minWidth: 150 }}
          >
            <MenuItem value="user">A person</MenuItem>
            <MenuItem value="role">Everyone with a role</MenuItem>
          </TextField>

          {subjectType === "user" ? (
            <PersonSelect
              people={people}
              value={subjectId}
              onChange={setSubjectId}
              label="Person"
              emptyLabel="Choose somebody"
              onSearch={(term) =>
                userApi
                  .directory(term)
                  .then((payload) => setPeople(withoutMe(payload.users, user?.id)))
                  .catch(() => {})
              }
              sx={{ flex: "1 1 200px", minWidth: 170 }}
            />
          ) : (
            <TextField
              select
              size="small"
              label="Role"
              value={subjectId}
              onChange={(event) => setSubjectId(event.target.value)}
              sx={{ flex: "1 1 200px", minWidth: 170 }}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={String(role.id)}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            size="small"
            label="They can"
            value={access}
            onChange={(event) => setAccess(event.target.value)}
            sx={{ flex: "1 1 170px", minWidth: 150 }}
          >
            <MenuItem value="use">Use it</MenuItem>
            <MenuItem value="edit">Use and change it</MenuItem>
          </TextField>
        </Stack>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={add} disabled={saving || !subjectId}>
              Share
            </Button>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" gutterBottom>
          Who else has this
        </Typography>

        {shares === null ? (
          <LoadingRows rows={2} height={28} />
        ) : shares.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Only you.
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {shares.map((share) => (
              <Stack key={share.id} direction="row" spacing={1} alignItems="center">
                {share.subject_type === "user" ? <UserAvatar name={share.subject_label} size={26} tooltip={false} /> : null}
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                  {share.subject_label}
                  {share.subject_type === "role" ? (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                      role
                    </Typography>
                  ) : null}
                </Typography>
                <Chip
                  size="small"
                  label={share.access === "edit" ? "Can change" : "Can use"}
                  variant="outlined"
                  sx={{ height: 20, fontSize: 10 }}
                />
                <Tooltip title="Take it back">
                  <IconButton size="small" onClick={() => remove(share)} aria-label={`Stop sharing with ${share.subject_label}`}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}

        <Alert severity="warning" sx={{ mt: 2 }}>
          Taking a share back stops them opening it here. If they have already read it, the password itself is still
          theirs to remember — change it as well.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}
